/*
 * Copyright (c) 2026 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Sunday, June 14th 2026, 3:37:52 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { BoundaryEventConfiguration, TaskTemplate } from "lib/schemas";
import { useWorkflowContext } from "service-builder/providers";
import { useDeleteBoundaryEvent } from "service-builder/services";

export const useBoundaryEventHandlers = () => {
  const {
    taskTemplateListContext: { findItem: findTaskTemplate, updateList: updateTaskTempateList },
  } = useWorkflowContext();

  const onDeleteBoundaryEvent = (
    deletedBoundaryEventId: BoundaryEventConfiguration["id"],
    hostNodeId: TaskTemplate["id"]
  ) => {
    const hostNode = findTaskTemplate(hostNodeId).item;
    const currentEventIds = hostNode.boundary_events ?? [];
    const currentEventsDetails = hostNode.boundary_events_detailed ?? [];
    const updatedHostNode = {
      ...hostNode,
      boundary_events: currentEventIds.filter((id) => deletedBoundaryEventId !== id),
      boundary_events_detailed: currentEventsDetails.filter(
        (boundaryEvent) => deletedBoundaryEventId !== boundaryEvent.id
      ),
    };
    updateTaskTempateList(updatedHostNode);
  };

  const onSaveBoundaryEvent = (updatedBoundaryEvent: BoundaryEventConfiguration) => {
    const hostNode = findTaskTemplate(updatedBoundaryEvent.host_node).item;
    const currentEventIds = hostNode.boundary_events ?? [];
    const currentEventsDetails = hostNode.boundary_events_detailed ?? [];
    updateTaskTempateList({
      ...hostNode,
      boundary_events: [...currentEventIds, updatedBoundaryEvent.id],
      boundary_events_detailed: currentEventsDetails.map((boundaryEvent) =>
        boundaryEvent.id === updatedBoundaryEvent.id ? updatedBoundaryEvent : boundaryEvent
      ),
    });
  };

  const getHostNode = (boundaryEvent: BoundaryEventConfiguration) => {
    return findTaskTemplate(boundaryEvent.target_node)?.item;
  };

  const getTargetNode = (boundaryEvent: BoundaryEventConfiguration) => {
    return findTaskTemplate(boundaryEvent.target_node)?.item;
  };

  const getBoundaryEventFromHostNodeId = (
    boundaryEventId: BoundaryEventConfiguration["id"],
    hostNodeId: TaskTemplate["id"]
  ) => {
    const hostNode = findTaskTemplate(hostNodeId)?.item;
    return hostNode?.boundary_events_detailed?.find((boundaryEvent) => boundaryEvent.id === boundaryEventId) || null;
  };

  const { deleteItem } = useDeleteBoundaryEvent();
  const deleteBoundaryEvent = React.useCallback(
    (deletedBoundaryEventId: TaskTemplate["boundary_events_detailed"][0]["id"], hostNodeId: TaskTemplate["id"]) => {
      if (!hostNodeId) return;
      deleteItem(deletedBoundaryEventId, {
        onDelete: (deletedBoundaryEventId) => onDeleteBoundaryEvent(deletedBoundaryEventId, hostNodeId),
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return {
    onSaveBoundaryEvent,
    onDeleteBoundaryEvent,
    getHostNode,
    getTargetNode,
    getBoundaryEventFromHostNodeId,
    deleteBoundaryEvent,
  };
};
